<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb 
          parentRouteName="RewardCardList" 
          :extra-items="isReadOnly ? [
            {
              text: form.title
            }
          ] : isEditing ? [
            {
              text: form.title,
              to: 'RewardCardView',
              params: $route.params.reward_card_id
            },
            '編輯集點卡'
          ] : [
            '新增集點卡'
          ]" 
        />
      </div>
    </div>

    <b-card>
      <b-overlay :show="showLoading">
        <h4 class="mb-3 font-weight-bold">{{ isReadOnly ? "檢視集點卡" : isEditing ? '編輯集點卡' : '新增集點卡' }}</h4>
        <div class="mb-2">
          <validation-error-alert
            v-if="validationErrors"
            :errors="validationErrors"
          ></validation-error-alert>
          <!-- 通路 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="通路"
            label-for="merchant"
          >
            <b-form-select
              v-model="form.merchant_id"
              :options="merchants"
              :disabled="false == merchantsAreFetched || isReadOnly"
            ></b-form-select>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.merchant_id.$error')">
              此欄位為必填
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 通路 End -->

          <!-- 分店 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="分店">
            <AppSingleSelect
              v-model="selected_branch"
              :options="branchOptions"
              :disabled="disabled_branch_select"
              @input="branchChange"
            ></AppSingleSelect>
          </b-form-group>
          <!-- 分店 End -->

          <!-- 集點卡名稱 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="集點卡名稱"
          >
            <b-form-input
              type="text"
              :state="deepGet(v$, 'form.title.$error') ? false: null"
              v-model="form.title"
              :readonly="isReadOnly"
            ></b-form-input>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.title.$error')">
              此欄位為必填
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 集點卡名稱 End -->
          <!-- 主圖 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
          >
            <template #label>
              主圖
              <p class="text-black-50 mt-2" style="font-size: .75rem">寬 x 高：1400px x 不限</p>
            </template>
            <template #default>
              <AppFileUploader
                v-model="form.config.main_image"
                placeholder-when-uploaded="（查看檔案）"
                placeholder-when-empty=""
              />
            </template>
          </b-form-group>
          <!-- 主圖 End -->
          <!-- 使用期間 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="使用期間"
          >
            <div class="d-flex align-items-center">
              <datepicker
                placeholder="選擇日期"
                v-model="form.start_at"
                bootstrap-styling
                :language="zh"
                format="yyyy-MM-dd"
                :disabled="isReadOnly"
                :clear-button="!isReadOnly"
                :input-class="deepGet(v$, 'form.start_at.$error') ? 'is-invalid' : ''"
              ></datepicker>
              <span class="mx-2">~</span>
              <datepicker
                placeholder="選擇日期"
                v-model="form.end_at"
                bootstrap-styling
                :language="zh"
                format="yyyy-MM-dd"
                :disabled="isReadOnly"
                :clear-button="!isReadOnly"
                :input-class="deepGet(v$, 'form.end_at.$error') ? 'is-invalid' : ''"
              ></datepicker>
            </div>
            <b-form-invalid-feedback
              :state="!deepGet(v$, 'form.start_at.beforeEndAt.$invalid')"
            >
              結束時間必須大於起始時間
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 使用期間 End -->
          <!-- 卡面點數 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="卡面點數"
          >
            <b-form-input
              type="text"
              :state="deepGet(v$, 'form.point_number.$error') ? false: null"
              v-model="form.point_number"
              :readonly="isReadOnly"
              style="width: 200px"
            ></b-form-input>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.point_number.$error')">
              此欄位為必填，數值在 1 ~ 10 之間
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 卡面點數 End -->
          <!-- 開卡贈送點數 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="開卡贈送點數"
          >
            <b-form-input
              type="text"
              :state="deepGet(v$, 'form.give_number.$error') ? false: null"
              v-model="form.give_number"
              :readonly="isReadOnly"
              style="width: 200px"
            ></b-form-input>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.give_number.$error')">
              開卡贈送點數需介於 0~卡面點數之間
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 開卡贈送點數 End -->
          <!-- 集點累積上限 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="集點累積上限"
          >
            <b-form-input
              type="text"
              :state="deepGet(v$, 'form.point_collection_limit.$error') ? false: null"
              v-model="form.point_collection_limit"
              :readonly="isReadOnly"
              placeholder="不填寫，表示不限制"
              style="width: 200px"
            ></b-form-input>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.point_collection_limit.$error')">
              集點累積上限須為數字
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 集點累積上限 End -->
          <!-- 呈現模式 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="呈現模式"
          >
            <b-form-radio-group
              v-model="form.show_used_point"
              :options="showUsedPointOptions"
              :disabled="isReadOnly"
              :state="deepGet(v$, 'form.show_used_point.$error') ? false: null"
            ></b-form-radio-group>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.show_used_point.$error')">
              此欄位為必填
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 呈現模式 End -->
          <!-- 顯示兌換獎項 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="顯示兌換獎項"
          >
            <b-form-radio-group
              v-model="form.config.show_gift_tab"
              :options="showOrHideOptions"
              :disabled="isReadOnly"
            ></b-form-radio-group>
          </b-form-group>
          <!-- 顯示兌換獎項 End -->
          <!-- 顯示兌換紀錄 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="顯示兌換紀錄"
          >
            <b-form-radio-group
              v-model="form.config.show_received_tab"
              :options="showOrHideOptions"
              :disabled="isReadOnly"
            ></b-form-radio-group>
          </b-form-group>
          <!-- 顯示兌換紀錄 End -->
          <!-- 顯示點數紀錄 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="顯示點數紀錄"
          >
            <b-form-radio-group
              v-model="form.config.show_point_log_tab"
              :options="showOrHideOptions"
              :disabled="isReadOnly"
            ></b-form-radio-group>
          </b-form-group>
          <!-- 顯示點數紀錄 End -->
          <!-- 獎項兌換類型 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="獎項兌換類型"
          >
            <b-form-radio-group
              v-model="form.config.redeem_type"
              :options="redeemTypeOptions"
              :disabled="isReadOnly"
              :state="deepGet(v$, 'form.config.redeem_type.$error') ? false: null"
            ></b-form-radio-group>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.config.redeem_type.$error')">
              此欄位為必填
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 獎項兌換類型 End -->
          <!-- 獎項兌換條件 Start 只有獎項兌換類型為商品時，才讓使用者選擇 -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="獎項兌換條件"
          >
            <b-form-radio-group
              v-model="form.config.check_type"
              :options="checkTypeOptions"
              :disabled="isReadOnly || form.config.gift_section.branch_list.enable"
              :state="deepGet(v$, 'form.config.check_type.$error') ? false: null"
            ></b-form-radio-group>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.config.check_type.$error')">
              此欄位為必填
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 獎項兌換條件 End -->
          <!-- 獎項兌換順序 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="獎項兌換順序"
          >
            <b-form-radio-group
              v-model="form.config.redemption_order_type"
              :options="redemptionOrderTypeOptions"
              :disabled="isReadOnly"
            ></b-form-radio-group>
          </b-form-group>
          <!-- 獎項兌換順序 End -->
          <!-- 參加權限 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="查看集點卡權限"
          >
            <b-form-checkbox v-model="form.config.permissions" value="registered" :disabled="isReadOnly">需註冊</b-form-checkbox>
          </b-form-group>
          <!-- 參加權限 End -->

          <!-- 集點權限 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="集點權限"
          >
            <b-form-checkbox v-model="form.config.collect_point_permissions" value="registered" :disabled="isReadOnly">需註冊</b-form-checkbox>
          </b-form-group>
          <!-- 集點權限 End -->

          <!-- 兌換權限 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="兌換權限"
          >
            <b-form-checkbox v-model="form.config.redeem_permissions" value="registered" :disabled="isReadOnly">需註冊</b-form-checkbox>
          </b-form-group>
          <!-- 兌換權限 End -->

          <!-- 活動方式 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="活動方式"
          >
            <b-form-radio-group
              v-model="form.event_setting"
              :options="eventSettingOptions"
              :disabled="!canModifyEventSetting || isReadOnly"
              :state="deepGet(v$, 'form.event_setting.$error') ? false : null"
            ></b-form-radio-group>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.event_setting.$error')">
              此欄位為必填
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 活動方式 End -->
          <!-- 活動代碼 Start -->
          <b-form-group
            v-if="showEventSelection"
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="活動代碼"
          >
            <AppSingleSelect
              v-if="form.event_setting === 'select'"
              v-model="selectedEvent"
              :options="eventOptions"
              :disabled="!canModifyEventSetting || isReadOnly"
              @input="onEventSelectChange"
            />
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.event_id.$error')">
              此欄位為必填
            </b-form-invalid-feedback>

            <b-form-input
              v-if="form.event_setting === 'manual'"
              :value="form.event_code"
              @input="onEventCodeInput"
              :readonly="!canModifyEventSetting || isReadOnly"
              placeholder="請輸入活動代碼"
            ></b-form-input>
            <b-form-invalid-feedback :state="!deepGet(v$, 'form.event_code.$error')">
              <template v-if="deepGet(v$, 'form.event_code.required.$invalid')">
                此欄位為必填
              </template>
              <template v-else-if="deepGet(v$, 'form.event_code.eventCodeFormat.$invalid')">
                僅允許輸入大寫英文、數字、底線(_)
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
          <!-- 活動代碼 End -->
          <!-- 注意事項 Start -->
          <b-form-group
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="注意事項"
          >
            <TrixEditor v-model="form.memo" :readonly="isReadOnly"/>
          </b-form-group>
          <!-- 注意事項 End -->
          <!-- 是否啟用分店下拉 Start -->
          <b-form-group
            v-if="hasAdvandePermission"
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="是否啟用分店下拉"
          >
            <b-form-checkbox
              v-model="form.config.gift_section.branch_list.enable"
              :value="true"
              :unchecked-value="false"
              :disabled="isReadOnly || form.config.check_type === 'need_branch_code'"
            >
            </b-form-checkbox>
          </b-form-group>
          <!-- 是否啟用分店下拉 End -->
          <!-- 分店下拉選單來源 Start -->
          <b-form-group
            v-if="hasAdvandePermission"
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="分店下拉選單來源"
          >
            <b-form-input
              type="text"
              v-model="form.config.gift_section.branch_list.branch_provider"
              :readonly="isReadOnly"
              style="width: 200px"
            ></b-form-input>
          </b-form-group>
          <!-- 分店下拉選單來源 End -->
          <!-- 分店下拉選單文字 Start -->
          <b-form-group
            v-if="hasAdvandePermission"
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="分店下拉選單文字"
          >
            <b-form-input
              type="text"
              v-model="form.config.gift_section.branch_list.default_select_text"
              :readonly="isReadOnly"
              style="width: 200px"
            ></b-form-input>
          </b-form-group>
          <!-- 分店下拉選單文字 End -->
          <!-- 是否顯示分店代號 Start -->
          <b-form-group
            v-if="hasAdvandePermission"
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="是否顯示分店代號"
          >
            <b-form-checkbox v-model="form.config.gift_section.branch_list.show_branch_code" :value="true" :unchecked-value="false" :disabled="isReadOnly"></b-form-checkbox>
          </b-form-group>
          <!-- 是否顯示分店代號 End -->
          <!-- 商品是否使用庫存 Start -->
          <b-form-group
            v-if="hasAdvandePermission"
            label-cols="12"
            label-cols-lg="2"
            label-size="sm"
            label="商品是否使用庫存"
          >
            <b-form-checkbox v-model="form.config.gift_section.enable_product_stock" :value="true" :unchecked-value="false" :disabled="isReadOnly"></b-form-checkbox>
          </b-form-group>
          <!-- 商品是否使用庫存 End -->
        </div>
        <!-- 欄位部分 End -->
        <!-- 表單底部 Start -->
        <div class="d-flex justify-content-center">
          <b-button
            class="mr-3"
            @click="cancel"
            variant="outline-danger"
          >
            返回
          </b-button>
          <b-button
            @click="submit"
            variant="success"
            v-if="false == isReadOnly"
          >
            儲存
          </b-button>
        </div>
        <!-- 表單底部 End -->
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { isBefore } from "date-fns";
import moment from "moment";
import { mapGetters, mapState } from 'vuex';
import Datepicker from "vuejs-datepicker";
import { zh } from "vuejs-datepicker/dist/locale";
import useVuelidate from "@vuelidate/core";
import { required, numeric, requiredIf } from "@vuelidate/validators";
import merchantApi from "@/apis/merchant";
import rewardCardApi from "@/apis/reward-card";
import eventApi from "@/apis/event";
import AppFileUploader from "@/components/AppFileUploader.vue";
import TrixEditor from "@/components/TrixEditor";
import deepGet from "@/utils/deepGet";
import * as consts from "@/consts";
import mergeDefaultAndDataModel from "@/utils/mergeDefaultAndDataModel";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import PermissionChecker from "@/utils/PermissionChecker";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    AppSingleSelect,
    Datepicker,
    AppFileUploader,
    TrixEditor,
    BreadCrumb
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        merchant_id: { required },
        title: { required },
        point_number: {
          required,
          between: (value) => value >= 1 && value <= 10,
        },
        give_number: {
          between: (value) => {
            if (!this.form.point_number || !value) {
              return true
            }
            value = parseInt(value);

            return value >= 0 && value <= this.form.point_number;
          },
        },
        show_used_point: { required },
        point_collection_limit: { numeric },
        start_at: {
          beforeEndAt: () => {
            if (!this.form.end_at || !this.form.start_at) {
              return true
            }

            return isBefore(new Date(this.form.start_at), new Date(this.form.end_at));
          },
        },
        event_setting: {
          required,
        },
        event_id: {
          required: requiredIf(() => this.form.event_setting === 'select')
        },
        event_code: {
          required: requiredIf(() => this.form.event_setting === 'manual'),
          eventCodeFormat: value => !value || /^[A-Z0-9_]*$/.test(value),
        },
        config: {
          check_type: { required },
          redeem_type: { required },
        }
      },
    };
  },
  data() {
    return {
      zh,
      showLoading: true,
      merchantsAreFetched: false,
      merchants: [],
      eventOptions: [],
      form: {
        merchant_id: null,
        title: null,
        start_at: null,
        end_at: null,
        point_number: null,
        give_number: null,
        show_used_point: 0,
        point_collection_limit: null,
        config: {
          main_image: null,
          check_type: 'default',
          redeem_type: 'product',
          redemption_order_type: 'none',
          show_gift_tab: true,
          show_received_tab: true,
          show_point_log_tab: true,
          permissions: [],
          collect_point_permissions: [],
          redeem_permissions: [],
          gift_section: {
            branch_list: {
              enable: false,
              branch_provider: "",
              show_branch_code: "",
              default_select_text: "",
            },
            enable_product_stock: false,
          },
        },
        layout_type: 'default',
        point_style: 'default',
        verify_type: 'branch_code',
        event_id: null,
        memo: null,
        branch_id: null,
        event_setting: 'none',
        event_code: null,
      },
      disabled_branch_select: true,
      selected_branch: {},
      validationErrors: null,
      showUsedPointOptions: [
        { value: 0, text: "只顯示未使用點數" },
        { value: 1, text: "保留使用過的點數" },
        { value: 2, text: "不顯示卡面"}
      ],
      showOrHideOptions: [
        { value: true, text: "顯示" },
        { value: false, text: "不顯示" },
      ],
      checkTypeOptions: [
        { value: 'default', text: "一般兌換" },
        { value: 'need_branch_code', text: "輸入店號兌換" },
      ],
      redeemTypeOptions: [
        { value: 'product', text: '商品' },
        { value: 'coupon', text: '優惠券' },
      ],
      redemptionOrderTypeOptions: [
        { value: 'none', text: '無兌換順序限制' },
        { value: 'sequential', text: '依照順序兌換' },
      ],
      eventSettingOptions: [
        { text: '無活動事件', value: 'none' },
        { text: '建立新活動事件', value: 'manual' },
        { text: '選擇活動事件', value: 'select' }
      ],
      selectedEvent: null,
    };
  },
  computed: {
    ...mapGetters('general', [
      'myBranches',
      'currentBranch'
    ]),
    ...mapGetters("route", ["routeQuery"]),
    ...mapState("general", ['defaultMerchantType', 'organization']),
    hasAdvandePermission() {
      return this.$permissions.has(this.$permissions.consts.REWARD_CARD_ADV_SETTING);
    },
    id() {
      return this.$route.params.reward_card_id;
    },
    isCreating() {
      return !this.id;
    },
    isEditing() {
      return !!this.id;
    },
    isReadOnly() {
      return this.$route.name == "RewardCardView";
    },
    branchOptions() {
      let options = [];

      if (this.checkPermission([consts.REWARD_CARD_LIST_NO_BRANCH])) {
        options = [
          {
            value: 'null',
            text: '品牌組織階層(無隸屬分店)'
          }
        ];
      }

      if (this.checkPermission([consts.REWARD_CARD_LIST_BRANCH]) || options.length === 0) {
        const option1 = this.myBranches.map(branch => {
          let branchName = `${branch.branch_code} ${branch.name}`;
          return {
            value: branch.id,
            text: branchName
          };
        });

        options = [
          ...options,
          ...option1 // ← 改這裡，把陣列展開
        ];
      }

      return options;
    },
    showEventSelection() {
      return this.form.event_setting !== 'none'
    },
    canModifyEventSetting() {
      return this.isCreating
        || (this.isEditing && this.$permissions.has(this.$permissions.consts.REWARD_CARD_EVENT_MODIFY));
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.fetchMerchants(),
        this.id ? this.fetchRewardCard() : () => {},
        this.fetchEvents(),
      ]);
    } catch (e) {
      console.error(e)
      this.$swal("錯誤", "讀取資料錯誤", "error");
      this.$router.replace({name: "RewardCardList", query: this.routeQuery});
    }

    // 有權限的話，可以編輯下拉
    if (this.checkPermission([consts.REWARD_CARD_LIST_BRANCH])) {
      this.disabled_branch_select = false;
    }

    if (!this.isEditing) {
      // 有 REDEEM_CODE_LIST_NO_BRANCH 的話預設選擇 品牌組織階層(無隸屬分店)
      if (this.checkPermission([consts.REWARD_CARD_LIST_NO_BRANCH])) {
        this.selected_branch = this.branchOptions.find(
          option => option.value === 'null'
        );
      } else {
        this.selected_branch = this.branchOptions.find(
          option => option.value === this.currentBranch.id
        );
      }

      this.form.branch_id = this.selected_branch.value;
    }

    // 初始化 selectedEvent 僅在編輯且 event_id 有值時
    if (this.isEditing && this.form.event_id) {
      this.selectedEvent = this.eventOptions.find(opt => opt.value === this.form.event_id) || null;
    }

    this.showLoading = false;
  },
  methods: {
    deepGet,
    mergeDefaultAndDataModel,
    branchChange(){
      this.form.branch_id = this.selected_branch?.value ?? null;
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    cancel() {
      this.$router.push({ name: 'RewardCardList', query: this.routeQuery });
    },
    async fetchMerchants() {
      this.merchantsAreFetched = false;
      const { data } = await merchantApi.list();
      this.merchantsAreFetched = true;

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: merchant.type_name
              ? `${merchant.name} (${merchant.type_name})`
              : merchant.name,
          };
        }),
      ];

      if (!this.id) {
        const defaultMerchant = data.data.find((merchant) => merchant.type === this.defaultMerchantType) ??
          data.data.find((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE) ;

        if (defaultMerchant && !this.form.merchant_id) {
          this.form.merchant_id = defaultMerchant.id;
        }
      }
    },
    async fetchRewardCard() {
      const { data } = await rewardCardApi.get(this.id);
      let form = this.mergeDefaultAndDataModel(this.form, data.data);
      form.event_setting = form.event_id ? 'select' : 'none';
      this.$set(this, 'form', form);

      const branchId = data?.data?.branch_id;
      const noBranchPermission = this.checkPermission([consts.REWARD_CARD_LIST_NO_BRANCH]);

      this.selected_branch =
        this.branchOptions.find(option => option.value === branchId) ||
        (noBranchPermission && this.branchOptions.find(option => option.value === 'null')) ||
        this.branchOptions.find(option => option.value === this.currentBranch.id);
    },
    async fetchEvents() {
      const { data } = await eventApi.getEvents(this.organization, { is_all: true });

      this.eventOptions = [
        ...data.data.map((event) => {
          return {
            value: event.id,
            text: `(${event.code}) ${event.name}`
          };
        }),
      ];
    },
    async submit() {
      const result = await this.v$.$validate();
      if (!result) return false;

      try {
        this.showLoading = true;

        // 將null轉換
        if (this.form.branch_id === 'null') {
          this.form.branch_id = null;
        }

        if (this.isEditing) {
          const response = await rewardCardApi.update({
            id: this.id,
            ...this.form,
            event_code: this.form.event_setting === 'manual' ? this.form.event_code : null,
            start_at: this.form.start_at ? moment(this.form.start_at).format("YYYY-MM-DD 00:00:00") : null,
            end_at: this.form.end_at ? moment(this.form.end_at).format("YYYY-MM-DD 23:59:59") : null,
          });

          if (response.status && response.status === 200) {
            this.$swal.fire({
              title: "更新成功",
              type: "success",
            });

            this.$router.push({name: "RewardCardList", query: this.routeQuery});
          }
        } else {
          const response = await rewardCardApi.store({
            ...this.form,
            event_code: this.form.event_setting === 'manual' ? this.form.event_code : null,
            start_at: this.form.start_at ? moment(this.form.start_at).format("YYYY-MM-DD 00:00:00") : null,
            end_at: this.form.end_at ? moment(this.form.end_at).format("YYYY-MM-DD 23:59:59") : null,
          });

          if (response.status && response.status >= 200) {
            this.$swal.fire({
              title: "新增成功",
              type: "success",
            });

            this.$router.push({name: "RewardCardList", query: this.routeQuery});
          }
        }
      } catch (error) {
        this.showLoading = false;

        if (error.response.status === 422 && error.response.data.message) {
          this.validationErrors = error.response.data.message;
          return
        }

        this.$swal.fire({
          type: "error",
          title: this.isEditing ? "更新失敗" : "新增失敗",
          text: error.response.data?.message || "請稍後再試",
        });
      }
    },
    onEventSelectChange(option) {
      this.selectedEvent = option;
      this.form.event_id = option ? option.value : null;
    },
    onEventCodeInput(value) {
      // 僅自動轉大寫，不做過濾，格式驗證交給 validations
      this.form.event_code = value.toUpperCase();
    },
  },
};
</script>
